import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuItem from '@mui/material/MenuItem';

import IconMenu from 'components/IconMenu';
import {
    useDeleteInscriptionComment,
    useUpdateInscriptionComment,
} from 'routes/BudgetModule/components/shared/InscriptionsGrid/InscriptionDetails/inscriptionCommentQuery';
import { styled } from '@mui/material/styles';
import Flex from 'ui/Flex/Flex';
import CommentTextField from './CommentTextField';
import { StyHeader, StyHighlightedText } from './StyledComponents';
import { useSelector } from 'store/hooks';

export type CommentEventProps = {
    user: {
        email: string;
    } | null;
    content: string;
    id: number;
    inscription: {
        budgetLineSoftwareId?: string;
        id?: number;
    };
};

const StyIconMenu = styled(IconMenu, {
    name: 'StyIconMenu',
})({
    marginTop: '7px',
});

function CommentEvent({
    user,
    content,
    id,
    inscription,
}: CommentEventProps): React.ReactElement {
    const { t } = useTranslation();
    const loggedInUserEmail = useSelector((state) => state.vision.user.email);
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState('');
    const [disableEditButton, setDisableEditButton] = useState(false);
    useEffect(() => {
        if (content !== editValue) setEditValue(content);
    }, [content]);
    const { mutate: deleteInscriptionComment } = useDeleteInscriptionComment(
        inscription.id,
    );
    const { mutate: updateInscriptionComment } = useUpdateInscriptionComment(
        setIsEditing,
        setDisableEditButton,
        inscription.id,
    );
    const actions = [
        {
            name: 'editComment',
            onClick: () => {
                setIsEditing(true);
            },
        },
        {
            name: 'deleteComment',
            onClick: () => deleteInscriptionComment(id),
        },
    ];

    const updateComment = () => {
        setDisableEditButton(true);
        updateInscriptionComment({ commentId: id, message: editValue });
    };

    const cancelEdit = () => {
        setIsEditing(false);
    };

    const userLabel = user?.email ?? t('common.unknown');

    return (
        <>
            <StyHeader>
                <StyHighlightedText>{userLabel}</StyHighlightedText>
                {t('budgetHistory.eventComment')}
            </StyHeader>
            {isEditing ? (
                <CommentTextField
                    setValue={setEditValue}
                    value={editValue}
                    sendComment={updateComment}
                    disableButton={disableEditButton || editValue === content}
                    isUpdate
                    cancel={cancelEdit}
                    canEdit
                />
            ) : (
                <CommentTextField
                    setValue={setEditValue}
                    value={content}
                    sendComment={updateComment}
                    disableButton={disableEditButton || editValue === content}
                    isUpdate
                    cancel={cancelEdit}
                >
                    {loggedInUserEmail === user?.email && (
                        <Flex>
                            <StyIconMenu iconElement={<MoreVertIcon />} small>
                                {actions.map((action) => (
                                    <MenuItem
                                        key={action.name}
                                        onClick={action.onClick}
                                    >
                                        {t(`budgetHistory.${action.name}`)}
                                    </MenuItem>
                                ))}
                            </StyIconMenu>
                        </Flex>
                    )}
                </CommentTextField>
            )}
        </>
    );
}
export default CommentEvent;
